import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

const normalizePermissionState = (status) => {
  if (!status || typeof status.state !== 'string') return 'unknown';
  return status.state;
};

const queryPermissionState = async () => {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }

  try {
    const status = await navigator.permissions.query({ name: 'microphone' });
    return normalizePermissionState(status);
  } catch (_) {
    return 'unknown';
  }
};

const isPermissionDeniedError = (error) => {
  const errorName = error?.name || '';
  const errorMessage = String(error?.message || '').toLowerCase();

  return (
    errorName === 'NotAllowedError' ||
    errorName === 'SecurityError' ||
    errorMessage.includes('permission denied') ||
    errorMessage.includes('permission dismissed') ||
    errorMessage.includes('not allowed')
  );
};

export const MicrophoneService = {
  async requestAudioStream(constraints = { audio: true }) {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      const error = new Error('Microphone recording is not supported on this device.');
      error.code = 'MICROPHONE_UNSUPPORTED';
      throw error;
    }

    const permissionStateBeforeRequest = await queryPermissionState();

    if (permissionStateBeforeRequest === 'denied') {
      const error = new Error('Microphone permission has already been denied.');
      error.code = 'MICROPHONE_PERMISSION_DENIED';
      error.permissionState = permissionStateBeforeRequest;
      throw error;
    }

    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      const permissionStateAfterRequest = await queryPermissionState();

      if (
        permissionStateAfterRequest === 'denied' ||
        isPermissionDeniedError(error)
      ) {
        error.code = 'MICROPHONE_PERMISSION_DENIED';
        error.permissionState = permissionStateAfterRequest;
      }

      throw error;
    }
  },

  getPermissionErrorMessage(error) {
    if (error?.code === 'MICROPHONE_UNSUPPORTED') {
      return 'Audio recording is not supported on this device.';
    }

    if (error?.code === 'MICROPHONE_PERMISSION_DENIED') {
      if (isNative()) {
        return 'Microphone access is blocked for this app. Please allow microphone access in your device app settings to use voice recording.';
      }

      return 'Microphone access was denied. Please allow microphone access in your browser settings to use voice recording.';
    }

    return 'Could not access microphone. Please check permissions and try again.';
  }
};
